// Google Maps Script Loader Utility
// Prevents multiple script loading and handles cleanup safely

let scriptLoadingPromise = null;
let scriptLoaded = false;

export const loadGoogleMapsScript = () => {
  // Check if API key is configured
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.error('Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY environment variable.');
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  // If already loaded, return resolved promise
  if (scriptLoaded && window.google && window.google.maps) {
    return Promise.resolve();
  }

  // If already loading, return existing promise
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    // Script exists but might not be loaded yet
    scriptLoadingPromise = new Promise((resolve) => {
      if (window.google && window.google.maps) {
        scriptLoaded = true;
        resolve();
      } else {
        const handleLoad = () => {
          scriptLoaded = true;
          resolve();
        };
        
        // Check if script is already loaded
        if (existingScript.complete || existingScript.readyState === 'complete') {
          handleLoad();
        } else {
          existingScript.addEventListener('load', handleLoad);
          existingScript.addEventListener('error', () => {
            console.warn('Existing Google Maps script failed to load');
            scriptLoadingPromise = null;
            resolve(); // Resolve anyway to prevent hanging
          });
        }
      }
    });
    return scriptLoadingPromise;
  }

  // Create new script loading promise
  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };

    script.onerror = () => {
      scriptLoadingPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };

    // Safely append script to head
    try {
      document.head.appendChild(script);
    } catch (error) {
      scriptLoadingPromise = null;
      reject(new Error(`Failed to append Google Maps script: ${error.message}`));
    }
  });

  return scriptLoadingPromise;
};

export const isGoogleMapsLoaded = () => {
  return scriptLoaded && window.google && window.google.maps;
};

export const waitForGoogleMaps = () => {
  return new Promise((resolve) => {
    if (isGoogleMapsLoaded()) {
      resolve();
    } else {
      const checkInterval = setInterval(() => {
        if (isGoogleMapsLoaded()) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    }
  });
};

export const cleanupGoogleMaps = () => {
  // Reset the loading state
  scriptLoadingPromise = null;
  scriptLoaded = false;
  
  // Note: We don't remove the script from DOM as it might be used by other components
  // The script will be cleaned up when the page is unloaded
}; 